import React, { useState, useEffect } from 'react';
import { FaTimes } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import useUploadFile from './useUploadFile';
import { MwSpinner } from '../components/ui';
import MwInputFile from '../components/ui/MwInputFile';

const MultipleFileUploader = ({ setAttachedFiles , attachedFiles  = [] }) => {
  const [t, i18n] = useTranslation('global');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [fileKeys, setFileKeys] = useState([]);
  const isRTL = i18n.dir() === 'rtl';

  // Custom hook for file uploading
  const {
    location,
    loading: uploadLoading,
    error: uploadError,
    handleUpload,
    resultdelete,
    loadingDeleting,
    handleDelete
  } = useUploadFile([
    'image/png', 
    'image/jpeg', 
    'image/jpg', 
    'image/gif', 
    'image/webp', 
    'image/svg+xml', 
    'image/bmp', 
    'image/tiff', 
    'application/pdf'
  ], 'invoices');

  // Update selected files when a new file is uploaded
  useEffect(() => {
    if (location) {
      // Add the new file to the list
      const newFileKey = location;
      setFileKeys(prevKeys => [...prevKeys, newFileKey]);
      
      // Get the last file that was added via the file input
      const lastUploadedFile = document.getElementById('file_upload').files[0];
      
      if (lastUploadedFile) {
        const newFile = {
          name: lastUploadedFile.name,
          type: lastUploadedFile.type,
          url: location
        };
        
        setSelectedFiles(prevFiles => [...prevFiles, newFile]);
        
        // Update the parent component with array of file URLs
        const updatedAttachFiles = [...fileKeys, newFileKey];
        setAttachedFiles(updatedAttachFiles);
      }
    }
  }, [location]);

  // Initialize files from props if available
  useEffect(() => {
    if (attachedFiles && Array.isArray(attachedFiles) && attachedFiles.length > 0) {
      // Set the fileKeys directly from the array
      setFileKeys([...attachedFiles]);
      
      // Create dummy file objects for display
      const dummyFiles = attachedFiles.map((fileUrl, index) => {
        let fileType = 'image/jpeg'; // افتراضي
        
        if (fileUrl.toLowerCase().includes('.pdf')) {
          fileType = 'application/pdf';
        } else if (fileUrl.toLowerCase().includes('.webp')) {
          fileType = 'image/webp';
        } else if (fileUrl.toLowerCase().includes('.svg')) {
          fileType = 'image/svg+xml';
        } else if (fileUrl.toLowerCase().includes('.gif')) {
          fileType = 'image/gif';
        } else if (fileUrl.toLowerCase().includes('.png')) {
          fileType = 'image/png';
        } else if (fileUrl.toLowerCase().includes('.bmp')) {
          fileType = 'image/bmp';
        } else if (fileUrl.toLowerCase().includes('.tiff') || fileUrl.toLowerCase().includes('.tif')) {
          fileType = 'image/tiff';
        }
        
        return {
          name: `File ${index + 1}`,
          type: fileType,
          url: fileUrl
        };
      });
      
      setSelectedFiles(dummyFiles);
    } else if (attachedFiles && typeof attachedFiles === 'string' && attachedFiles.length > 0) {
      // Handle the case where attachedFiles might still be a string
      try {
        // Try to parse it as JSON first
        const filesArray = JSON.parse(attachedFiles);
        if (Array.isArray(filesArray)) {
          setFileKeys([...filesArray]);
          
          const dummyFiles = filesArray.map((fileUrl, index) => ({
            name: `File ${index + 1}`,
            type: fileUrl.toLowerCase().includes('.pdf') ? 'application/pdf' : 'image/jpeg',
            url: fileUrl
          }));
          
          setSelectedFiles(dummyFiles);
        }
      } catch (e) {
        // If not valid JSON, treat as comma-separated string
        const filesArray = attachedFiles.split(',').filter(file => file.trim() !== '');
        
        if (filesArray.length > 0) {
          setFileKeys(filesArray);
          
          const dummyFiles = filesArray.map((fileUrl, index) => ({
            name: `File ${index + 1}`,
            type: fileUrl.toLowerCase().includes('.pdf') ? 'application/pdf' : 'image/jpeg',
            url: fileUrl
          }));
          
          setSelectedFiles(dummyFiles);
        }
      }
    }
  }, [attachedFiles]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleUpload(e);
    }
  };

  const handleRemoveFile = async (index) => {
    if (index >= 0 && index < fileKeys.length) {
      const fileKeyToRemove = fileKeys[index];
      
      try {
        await handleDelete(fileKeyToRemove);
        
        // Remove the file from state
        const updatedFiles = [...selectedFiles];
        updatedFiles.splice(index, 1);
        setSelectedFiles(updatedFiles);
        
        const updatedKeys = [...fileKeys];
        updatedKeys.splice(index, 1);
        setFileKeys(updatedKeys);
        
        // Update the parent component with the new array
        setAttachedFiles(updatedKeys);
        
        // Revoke object URL if it exists
        if (selectedFiles[index]?.url && selectedFiles[index].url.startsWith('blob:')) {
          URL.revokeObjectURL(selectedFiles[index].url);
        }
        
        toast.success(t('deleted.file_deleted_successfully') || 'File deleted successfully', {
          position: "top-center",
          autoClose: 3000,
          rtl: isRTL,
          theme: "colored",
        });
      } catch (error) {
        toast.error(t('deleted.error_delete_file') || 'Error deleting file', {
          position: "top-center",
          autoClose: 3000,
          rtl: isRTL,
          theme: "colored",
        });
      }
    }
  };

  // Reset the file input after upload
  useEffect(() => {
    if (!uploadLoading && location) {
      const fileInput = document.getElementById('file_upload');
      if (fileInput) {
        fileInput.value = '';
      }
    }
  }, [uploadLoading, location]);

  return (
    <div className="border-2 border-dashed  border-gray-300 rounded-lg p-4 bg-white">
      <MwInputFile
        onChangeHandler={handleFileChange}
        acceptTypes="image/*, .webp, .svg, .bmp, .tiff, application/pdf"
        label={t('requisition.upload_file') || "Upload File"}
        id="file_upload"
        isMultiple={false}
        isSmall={false}
        disabled={uploadLoading}
      />
      
      {uploadLoading && (
        <div className="mt-4 flex justify-center">
          <MwSpinner size="md" />
        </div>
      )}
      
      {selectedFiles.length > 0 && (
        <div className="mt-4 space-y-4">
          <h3 className="text-sm font-medium text-gray-700">{t('requisition.selected_files') || "Uploaded Files"}:</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {selectedFiles.map((file, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-gray-800 truncate">{file.name}</p>
                  <button
                    onClick={() => handleRemoveFile(index)}
                    className="p-1 hover:bg-red-50 rounded-full text-red-500 hover:text-red-700 transition-colors"
                    title={t('requisition.remove_file') || "Remove file"}
                    disabled={loadingDeleting}
                  >
                    <FaTimes size={16} />
                  </button>
                </div>
                
                <div className="mt-2">
                  {file.type.startsWith('image/') ? (
                    <img 
                      src={file.url} 
                      alt="Preview" 
                      className="max-h-32 max-w-full mx-auto object-contain rounded" 
                    />
                  ) : file.type === 'application/pdf' && (
                    <div className="flex items-center justify-center">
                      <iframe 
                        src={file.url} 
                        className="w-full h-24" 
                        title="PDF Preview"
                      ></iframe>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default MultipleFileUploader;